import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { cachedFetch, postWithRetry } from "../lib/apiResilient";
import { motion, AnimatePresence, useScroll, useTransform, useInView, useMotionValue, useSpring } from "framer-motion";
import {
  ArrowUpRight,
  Sparkles,
  Search as SearchIcon,
  X,
  Check,
  ChevronRight,
  Compass,
  BookOpen,
  Flame,
  Crown,
  Zap,
  Bookmark,
  BookmarkCheck,
  Loader2,
} from "lucide-react";
import { Reveal } from "../components/Reveal";
import { useSaved } from "../lib/useSaved";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Category icon palette
const CAT_ICON = {
  brand:   Crown,
  sell:    Flame,
  ai:      Zap,
  content: Sparkles,
  mind:    Compass,
  build:   BookOpen,
};

function formatPrice(p) {
  if (p === 0) return "Free";
  return `$${Number(p).toLocaleString()}`;
}

// ────────────────────────────────────────────
// COUNT-UP — animates a number from 0 to target when scrolled into view.
// Editorial counter for the masthead and stats strips.
// ────────────────────────────────────────────
function CountUp({ to = 0, duration = 1.4, className = "", testid }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { duration: duration * 1000, bounce: 0 });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (inView) mv.set(to);
  }, [inView, to, mv]);
  useEffect(() => {
    const unsub = spring.on("change", (v) => setVal(Math.round(v)));
    return () => unsub();
  }, [spring]);
  return <span ref={ref} data-testid={testid} className={className}>{val}</span>;
}

// ────────────────────────────────────────────
// TILT CARD — gyroscope-style 3D tilt on hover. Pure CSS perspective +
// motion springs. Drops the card back to flat smoothly when cursor exits.
// ────────────────────────────────────────────
function TiltCard({ children, className = "", testid, max = 8 }) {
  const ref = useRef(null);
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const sx = useSpring(rotateX, { stiffness: 200, damping: 22, mass: 0.6 });
  const sy = useSpring(rotateY, { stiffness: 200, damping: 22, mass: 0.6 });
  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    rotateY.set((px - 0.5) * 2 * max);
    rotateX.set(-(py - 0.5) * 2 * max);
  };
  const onLeave = () => { rotateX.set(0); rotateY.set(0); };
  return (
    <motion.div
      ref={ref}
      data-testid={testid}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: sx, rotateY: sy, transformPerspective: 900, transformStyle: "preserve-3d" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ────────────────────────────────────────────
// LIBRARY CARD INLINE FORM
function LibraryCardInlineForm() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Enter the email you used at checkout.");
      return;
    }
    setBusy(true);
    try {
      await postWithRetry(`${API}/api/library/signin-by-email`,
        { email: email.trim().toLowerCase() },
        { timeout: 10000, withCredentials: true }
      );
      toast.success("Welcome back. Opening your shelf.");
      navigate("/library");
    } catch (err) {
      const code = err?.response?.status;
      if (code === 404 || code === 403) {
        toast.error("We couldn't find a purchase on that email. Try the email you used at checkout.");
      } else {
        toast.error("Couldn't open your shelf just now. Try again.");
      }
      setBusy(false);
    }
  };
  return (
    <form
      onSubmit={onSubmit}
      data-testid="library-card-inline"
      className="relative bg-cream text-ink p-6 sm:p-8 border border-cream/20 shadow-2xl"
    >
      {/* Editorial decorative stamp */}
      <div className="absolute -top-3 -right-3 bg-ink text-cream px-3 py-1 overline text-[9px] tracking-[0.3em] rotate-3">
        Library Card
      </div>
      <div className="flex items-baseline justify-between mb-5">
        <p className="overline text-ink/55 text-[10px] tracking-[0.3em]">M Media · Studio</p>
        <p className="font-serif italic text-ink/60 text-xs">Member-only</p>
      </div>
      <h3 className="font-display text-2xl sm:text-3xl leading-[0.95] mb-4">
        Open my <span className="font-serif italic font-light">reading room.</span>
      </h3>
      <label className="block">
        <span className="overline text-ink/55 text-[10px] tracking-[0.28em]">Your email</span>
        <input
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@studio.com"
          data-testid="library-card-inline-input"
          className="w-full mt-2 bg-transparent border-b border-ink/30 focus:border-ink focus:outline-none px-1 py-2 text-base placeholder:text-ink/30"
        />
      </label>
      <button
        type="submit"
        disabled={busy}
        data-testid="library-card-inline-submit"
        className="mt-6 w-full inline-flex items-center justify-center gap-3 bg-ink text-cream px-6 py-4 overline text-[10px] tracking-[0.28em] hover:bg-ink/85 disabled:opacity-60 transition-colors"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <ArrowUpRight className="w-4 h-4"/>}
        {busy ? "Opening your shelf…" : "Enter the reading room"}
      </button>
      <p className="text-[10px] text-ink/45 italic mt-3 text-center leading-relaxed">
        No password. No email round-trip. Your shelf opens instantly.
      </p>
    </form>
  );
}


// ────────────────────────────────────────────
// PRODUCT CARD
// ────────────────────────────────────────────
function ProductCard({ p, idx, isSaved, onToggleSave, bundleMode, selected, onToggleBundle, disabledForBundle }) {
  const Icon = CAT_ICON[p.category] || BookOpen;
  // In bundle mode: render a button card with checkbox (no navigation)
  if (bundleMode) {
    const ineligible = disabledForBundle && !selected;
    return (
      <Reveal delay={(idx % 8) * 0.03}>
        <button
          type="button"
          data-testid={`bundle-pick-${p.slug}`}
          disabled={ineligible}
          onClick={() => onToggleBundle(p.slug)}
          className={`group block h-full text-left w-full transition-all ${ineligible ? "opacity-40 cursor-not-allowed" : ""}`}
        >
          <div className={`relative overflow-hidden bg-white border-2 transition-colors duration-300 h-full flex flex-col ${selected ? "border-ink ring-2 ring-ink/20" : "border-ink/10 hover:border-ink/40"}`}>
            <div className="relative aspect-[4/5] overflow-hidden bg-cream/50">
              <img
                src={`${API}${p.cover_url}`}
                alt={p.title}
                loading="lazy"
                onError={(e) => { e.target.style.display = "none"; }}
                className={`w-full h-full object-cover transition-all duration-500 ${selected ? "" : "grayscale group-hover:grayscale-0"}`}
              />
              {/* Checkbox overlay */}
              <div className={`absolute top-3 right-3 w-10 h-10 grid place-items-center transition-colors ${selected ? "bg-ink text-cream" : "bg-cream/95 text-ink/30 group-hover:text-ink"}`}>
                {selected ? <Check className="w-5 h-5" strokeWidth={2}/> : <span className="text-xs tracking-widest">+</span>}
              </div>
              <div className="absolute top-3 left-3 bg-cream/90 backdrop-blur px-2 py-1">
                <p className="text-[9px] tracking-[0.25em] uppercase text-ink font-medium">No. {p.edition_no}</p>
              </div>
              <div className="absolute bottom-3 left-3 bg-ink text-cream px-2.5 py-1">
                <p className="text-[10px] tracking-[0.2em] uppercase">{formatPrice(p.price)}</p>
              </div>
            </div>
            <div className="p-5 flex-1 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <Icon className="w-3 h-3 text-ink/60" strokeWidth={1.5}/>
                <p className="text-[9px] tracking-[0.3em] uppercase text-ink/60">{p.category_label} · {p.tier}</p>
              </div>
              <h3 className="font-display text-2xl leading-tight text-ink mb-2">{p.title}</h3>
              <p className="text-sm text-ink/70 leading-relaxed">{p.blurb}</p>
            </div>
          </div>
        </button>
      </Reveal>
    );
  }
  return (
    <Reveal delay={(idx % 8) * 0.03}>
      <Link
        to={`/studio/${p.slug}`}
        data-testid={`product-card-${p.slug}`}
        className="group block h-full"
      >
        <div className="relative overflow-hidden bg-white border border-ink/10 hover:border-ink/40 transition-colors duration-500 h-full flex flex-col">
          {/* Cover */}
          <div className="relative aspect-[4/5] overflow-hidden bg-cream/50">
            <img
              src={`${API}${p.cover_url}`}
              alt={p.title}
              loading="lazy"
              onError={(e) => { e.target.style.display = "none"; }}
              className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
            />
            {/* Edition seal */}
            <div className="absolute top-3 left-3 bg-cream/90 backdrop-blur px-2 py-1">
              <p className="text-[9px] tracking-[0.25em] uppercase text-ink font-medium">
                No. {p.edition_no}
              </p>
            </div>
            {/* Save button */}
            <button
              type="button"
              data-testid={`save-${p.slug}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleSave(p.slug); }}
              className="absolute top-3 right-3 w-9 h-9 grid place-items-center bg-cream/90 backdrop-blur hover:bg-cream transition-colors"
              aria-label="Save"
            >
              {isSaved
                ? <BookmarkCheck className="w-4 h-4 text-ink" strokeWidth={1.4}/>
                : <Bookmark className="w-4 h-4 text-ink" strokeWidth={1.4}/>}
            </button>
            {/* Free/Price tag */}
            <div className="absolute bottom-3 left-3 bg-ink text-cream px-2.5 py-1">
              <p className="text-[10px] tracking-[0.2em] uppercase">
                {formatPrice(p.price)}
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="p-5 flex-1 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <Icon className="w-3 h-3 text-ink/60" strokeWidth={1.5}/>
              <p className="text-[9px] tracking-[0.3em] uppercase text-ink/60">
                {p.category_label} · {p.tier}
              </p>
            </div>
            <h3 className="font-display text-2xl leading-tight text-ink mb-2 group-hover:translate-x-1 transition-transform duration-500">
              {p.title}
            </h3>
            <p className="text-sm text-ink/70 leading-relaxed mb-4 flex-1">
              {p.blurb}
            </p>
            <div className="flex items-center justify-between text-xs text-ink mt-auto pt-3 border-t border-ink/10">
              <span className="tracking-[0.2em] uppercase">
                {p.is_free ? "Get free" : "Enter"}
              </span>
              <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-500"/>
            </div>
          </div>
        </div>
      </Link>
    </Reveal>
  );
}

// ────────────────────────────────────────────
// QUIZ
// ────────────────────────────────────────────
const QUIZ_QUESTIONS = [
  {
    id: "goal",
    title: "Where do you most want movement in the next 90 days?",
    options: [
      { v: "audience",  label: "Build an audience that respects me" },
      { v: "income",    label: "Lift income — predictably" },
      { v: "operate",   label: "Run my business without it owning me" },
      { v: "launch",    label: "Launch a new product or offer" },
      { v: "ai_speed",  label: "Make AI actually pay off" },
    ],
  },
  {
    id: "stage",
    title: "Where are you in the journey?",
    options: [
      { v: "idea",       label: "Idea / early build" },
      { v: "first_100",  label: "First 100 customers" },
      { v: "scaling",    label: "Scaling past $250K" },
      { v: "leveling",   label: "Leveling up the brand itself" },
    ],
  },
  {
    id: "block",
    title: "What's actually in the way?",
    options: [
      { v: "voice",     label: "Voice & positioning" },
      { v: "closing",   label: "Closing the deal" },
      { v: "time",      label: "Time, focus, energy" },
      { v: "tooling",   label: "Tools, systems, AI" },
      { v: "content",   label: "Content & social output" },
    ],
  },
  {
    id: "spend",
    title: "Comfortable starting budget?",
    options: [
      { v: "low",   label: "Under $50" },
      { v: "mid",   label: "$50 — $250" },
      { v: "high",  label: "Open to $500+" },
    ],
  },
  {
    id: "pace",
    title: "Preferred pace?",
    options: [
      { v: "weekend",    label: "A guide I can read this weekend" },
      { v: "intensive",  label: "A full intensive I'll commit to" },
    ],
  },
];

function StudioQuiz({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) { setStep(0); setAnswers({}); setResult(null); }
  }, [open]);

  if (!open) return null;
  const q = QUIZ_QUESTIONS[step];
  const isLast = step === QUIZ_QUESTIONS.length - 1;
  const progress = Math.round(((step + (answers[q?.id] ? 1 : 0)) / QUIZ_QUESTIONS.length) * 100);

  const pick = async (v) => {
    const next = { ...answers, [q.id]: v };
    setAnswers(next);
    if (!isLast) {
      setTimeout(() => setStep(s => s + 1), 200);
    } else {
      setLoading(true);
      try {
        const { data } = await axios.post(`${API}/api/studio/quiz`, { answers: next });
        setResult(data);
      } catch {
        toast.error("Couldn't compute recommendations. Try again.");
      }
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur"
        onClick={onClose}
      >
        <div
          className="fixed inset-0 overflow-y-auto p-4 sm:p-8 grid place-items-center"
          onClick={(e) => e.stopPropagation()}
        >
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="w-full max-w-3xl bg-cream relative"
          >
            <button
              type="button"
              data-testid="quiz-close"
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 grid place-items-center hover:bg-ink/5"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-ink"/>
            </button>

            {/* Progress */}
            <div className="h-[2px] bg-ink/10">
              <motion.div
                animate={{ width: `${progress}%` }}
                className="h-full bg-ink"
                transition={{ duration: 0.4 }}
              />
            </div>

            <div className="p-8 sm:p-14">
              {!result ? (
                <>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-ink/60 mb-4">
                    Find Your Path · Question {step + 1} of {QUIZ_QUESTIONS.length}
                  </p>
                  <h2 className="font-display text-3xl sm:text-5xl leading-tight text-ink mb-10">
                    {q.title}
                  </h2>
                  <div className="space-y-3">
                    {q.options.map((opt) => (
                      <button
                        key={opt.v}
                        type="button"
                        data-testid={`quiz-option-${q.id}-${opt.v}`}
                        disabled={loading}
                        onClick={() => pick(opt.v)}
                        className={`group w-full text-left flex items-center justify-between gap-4 border border-ink/20 hover:border-ink hover:bg-ink hover:text-cream transition-all duration-300 px-6 py-5 ${
                          answers[q.id] === opt.v ? "bg-ink text-cream border-ink" : "bg-cream text-ink"
                        }`}
                      >
                        <span className="font-serif italic text-lg sm:text-xl">{opt.label}</span>
                        <ChevronRight className="w-5 h-5 group-hover:translate-x-2 transition-transform"/>
                      </button>
                    ))}
                  </div>

                  <div className="mt-10 flex items-center justify-between text-xs text-ink/60">
                    <button
                      type="button"
                      disabled={step === 0}
                      onClick={() => setStep((s) => Math.max(0, s - 1))}
                      className="tracking-[0.2em] uppercase hover:text-ink disabled:opacity-40"
                    >
                      ← Back
                    </button>
                    {loading && <span>Reading the room…</span>}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-ink/60 mb-3">
                    Your Path
                  </p>
                  <h2 className="font-display text-4xl sm:text-6xl leading-[0.95] text-ink mb-3">
                    {result.top_category_label}
                  </h2>
                  <p className="font-serif italic text-xl text-ink/70 mb-10">
                    {result.top_category_subtitle}
                  </p>

                  <p className="text-[10px] tracking-[0.3em] uppercase text-ink/60 mb-4">
                    Start here · 3 editions picked for you
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                    {result.picks.map((p, i) => (
                      <Link
                        key={p.slug}
                        to={`/studio/${p.slug}`}
                        data-testid={`quiz-pick-${p.slug}`}
                        className="group block bg-white border border-ink/10 hover:border-ink/50 p-5 transition-colors"
                      >
                        <p className="text-[9px] tracking-[0.25em] uppercase text-ink/60 mb-3">
                          No. {p.edition_no} · {p.tier}
                        </p>
                        <h3 className="font-display text-xl text-ink leading-tight mb-2 group-hover:translate-x-1 transition-transform">
                          {p.title}
                        </h3>
                        <p className="text-xs text-ink/60 mb-4 line-clamp-3">{p.blurb}</p>
                        <div className="flex items-center justify-between border-t border-ink/10 pt-3 mt-auto">
                          <span className="text-xs tracking-[0.2em] uppercase">
                            {formatPrice(p.price)}
                          </span>
                          <ArrowUpRight className="w-3.5 h-3.5"/>
                        </div>
                      </Link>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      data-testid="quiz-retake"
                      onClick={() => { setStep(0); setAnswers({}); setResult(null); }}
                      className="px-6 py-3 border border-ink/30 text-xs tracking-[0.2em] uppercase hover:bg-ink hover:text-cream transition-colors"
                    >
                      Re-take the quiz
                    </button>
                    <button
                      type="button"
                      data-testid="quiz-done"
                      onClick={onClose}
                      className="px-6 py-3 bg-ink text-cream text-xs tracking-[0.2em] uppercase hover:bg-ink/80 transition-colors"
                    >
                      Browse the full library →
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// ────────────────────────────────────────────
// MAIN STUDIO PAGE
// ────────────────────────────────────────────
export default function Studio() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quizOpen, setQuizOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [tierFilter, setTierFilter] = useState("all");
  // ── Pick-5 bundle state ──
  const [bundleMode, setBundleMode] = useState(false);
  const [bundleSelection, setBundleSelection] = useState([]);
  const [bundleModalOpen, setBundleModalOpen] = useState(false);
  const toggleBundle = useCallback((slug) => {
    setBundleSelection((cur) => {
      if (cur.includes(slug)) return cur.filter(s => s !== slug);
      if (cur.length >= 5) return cur;
      return [...cur, slug];
    });
  }, []);
  const exitBundle = useCallback(() => {
    setBundleMode(false);
    setBundleSelection([]);
  }, []);
  const { saved, isSaved, toggle } = useSaved();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // 🎯 POST-PURCHASE UPSELL: if URL has ?upsell_slug=<slug>, enter bundle mode
  // with that slug pre-selected. Buyer only needs to pick 4 more.
  const upsellSlug = searchParams.get("upsell_slug");
  useEffect(() => {
    if (!upsellSlug || products.length === 0) return;
    const exists = products.find(p => p.slug === upsellSlug && !p.is_free && !p.no_download);
    if (!exists) {
      // Invalid slug — clean up the param so we don't keep retrying.
      const next = new URLSearchParams(searchParams);
      next.delete("upsell_slug");
      setSearchParams(next, { replace: true });
      return;
    }
    setBundleMode(true);
    setBundleSelection((cur) => (cur.includes(upsellSlug) ? cur : [upsellSlug]));
    // Scroll to catalog after a tick so layout settles
    setTimeout(() => {
      const el = document.getElementById("library");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    toast.success("Pick 4 more editions — your bundle saves you $36+.");
  }, [upsellSlug, products, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, fromCache } = await cachedFetch(
          "studio_catalog",
          `${API}/api/studio/products`,
        );
        if (cancelled) return;
        setProducts(data.products || []);
        setCategories(data.categories || []);
        if (fromCache) {
          // Silent — the page still works. We refresh on next visit.
          console.info("[studio] served catalog from cache (network blip)");
        }
      } catch {
        if (!cancelled) toast.error("Connection blip — please refresh.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const stats = useMemo(() => ({
    total: products.length,
    free: products.filter(p => p.is_free).length,
    paid: products.filter(p => !p.is_free).length,
  }), [products]);

  const featured = useMemo(() => {
    // Hand-curated edition numbers — top of each category
    const ids = ["004", "008", "020", "022", "024", "026", "037", "041"];
    return ids.map(id => products.find(p => p.edition_no === id)).filter(Boolean);
  }, [products]);

  const freeStarters = useMemo(() => products.filter(p => p.is_free), [products]);

  const filtered = useMemo(() => {
    let arr = products;
    if (activeCat !== "all" && activeCat !== "saved" && activeCat !== "free") {
      arr = arr.filter(p => p.category === activeCat);
    }
    if (activeCat === "saved") arr = arr.filter(p => saved.includes(p.slug));
    if (activeCat === "free") arr = arr.filter(p => p.is_free);
    if (tierFilter !== "all") arr = arr.filter(p => p.tier === tierFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.blurb.toLowerCase().includes(q) ||
        (p.tier || "").toLowerCase().includes(q) ||
        (p.category_label || "").toLowerCase().includes(q)
      );
    }
    return arr;
  }, [products, activeCat, tierFilter, search, saved]);

  const tiers = useMemo(() => {
    const set = new Set(products.map(p => p.tier));
    return ["all", ...Array.from(set)];
  }, [products]);

  // SEO: set document title + meta description for search engines
  useEffect(() => {
    const prev = document.title;
    document.title = "The M Media Studio · 45 Editions on PR, Branding & Marketing";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    const prevContent = meta.getAttribute('content');
    meta.setAttribute('content',
      "Forty-five editorial guides, workbooks and mini-courses on PR, branding, content marketing, and social. Instant in-browser access. Ten free starters. From the desk that's served brands like Susan Olsen, Julia Post and 100+ founders."
    );
    return () => { document.title = prev; if (prevContent) meta.setAttribute('content', prevContent); };
  }, []);

  // Rotating "now reading" ticker — quietly social-proofs the catalog
  const tickerLines = useMemo(() => {
    if (products.length === 0) return [];
    const titles = products.filter(p => !p.no_download).map(p => p.title);
    return [
      `Olivia from Newport just unlocked № ${products[0]?.edition_no || "001"}`,
      `38 founders read "${titles[3] || ""}" this week`,
      `Editor's pick today — "${titles[7] || ""}"`,
      `Bundle savings: $129 for any 5 paid editions`,
      `New on the shelf — "${titles[titles.length - 1] || ""}"`,
      `10% of every sale → charity partner`,
    ];
  }, [products]);
  const [tickerIdx, setTickerIdx] = useState(0);
  useEffect(() => {
    if (tickerLines.length === 0) return;
    const t = setInterval(() => setTickerIdx(i => (i + 1) % tickerLines.length), 3800);
    return () => clearInterval(t);
  }, [tickerLines.length]);

  // Big cover edition for the hero — pick the highest-numbered paid edition
  const coverEdition = useMemo(() => {
    const paid = products.filter(p => !p.is_free && !p.no_download);
    if (paid.length === 0) return null;
    return [...paid].sort((a, b) =>
      parseInt(b.edition_no || "0", 10) - parseInt(a.edition_no || "0", 10)
    )[0];
  }, [products]);

  // Scroll parallax — the cover card drifts upward subtly as the user scrolls
  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const coverY = useTransform(heroProgress, [0, 1], [0, -120]);

  return (
    <div className="bg-cream min-h-screen text-ink overflow-x-hidden">
      <Toaster />

      {/* ───── EDITORIAL MASTHEAD ─────
          Built to read like a Vogue cover page — issue number, big serif
          title, a featured edition "cover slot", live activity ticker. */}
      <section ref={heroRef} className="relative bg-cream overflow-hidden" data-testid="studio-hero"
        onMouseMove={(e) => {
          // Animate a soft spotlight that follows the cursor on the hero.
          const el = e.currentTarget;
          const r = el.getBoundingClientRect();
          el.style.setProperty("--mx", `${e.clientX - r.left}px`);
          el.style.setProperty("--my", `${e.clientY - r.top}px`);
        }}
        style={{
          backgroundImage: "radial-gradient(600px circle at var(--mx, -200px) var(--my, -200px), rgba(0,0,0,0.04), transparent 60%)",
        }}
      >
        {/* Ghosted edition-number marquee in the background — drifts L→R slowly */}
        {products.length > 0 && (
          <div className="absolute inset-x-0 top-[30%] sm:top-[40%] pointer-events-none select-none z-0 overflow-hidden" aria-hidden="true">
            <motion.div
              className="flex gap-12 whitespace-nowrap text-ink/[0.035] font-display text-[12rem] sm:text-[18rem] leading-none"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ duration: 80, ease: "linear", repeat: Infinity }}
            >
              {[...products, ...products].map((p, i) => (
                <span key={`bg-${i}`} className="flex-shrink-0">№{p.edition_no}</span>
              ))}
            </motion.div>
          </div>
        )}

        {/* Mast strip — Issue · Volume · Date */}
        <div className="relative z-10 border-b border-ink/15 pt-28 sm:pt-32">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-baseline justify-between gap-4 flex-wrap">
            <p className="overline text-ink/65 text-[10px] tracking-[0.4em] uppercase">
              The M Media Studio · Issue 02 · {new Date().toLocaleString("en-US", { month: "long", year: "numeric" })}
            </p>
            <p className="font-serif italic text-[12px] sm:text-[13px] text-ink/65">
              <CountUp to={stats.total} testid="stat-total"/> editions in print — <CountUp to={stats.free}/> complimentary, <CountUp to={stats.paid}/> priced fairly.
            </p>
          </div>
        </div>

        {/* ── Magazine cover grid ── */}
        <div className="relative z-10 max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-24">
          <div className="grid grid-cols-12 gap-6 lg:gap-12 items-start">

            {/* LEFT — cover headline */}
            <div className="col-span-12 lg:col-span-7">
              {/* Spinning seal */}
              <Reveal>
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <motion.div
                    aria-hidden="true"
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 18, ease: "linear" }}
                    className="w-8 h-8 rounded-full border border-ink/30 grid place-items-center text-[8px] tracking-[0.3em] text-ink/55"
                  >
                    ★
                  </motion.div>
                  <span className="overline text-ink/60 text-[10px] tracking-[0.4em]">
                    From the desk · in print since 2014
                  </span>
                </div>
              </Reveal>

              {/* Massive cover headline */}
              <Reveal delay={0.05}>
                <h1
                  className="font-display tracking-tighter leading-[0.84] text-[16vw] sm:text-[10vw] lg:text-[8.6vw] xl:text-[9rem] text-ink uppercase break-words"
                  data-testid="studio-hero-title"
                >
                  Sharper.
                  <br/>
                  <span className="font-serif italic font-light normal-case text-ink/95">
                    in days,
                  </span>
                  <br/>
                  <span className="font-serif italic font-light normal-case text-ink/95">
                    not years.
                  </span>
                </h1>
              </Reveal>

              {/* Editorial dek */}
              <Reveal delay={0.18}>
                <p className="mt-6 sm:mt-8 max-w-xl text-ink/75 leading-relaxed text-base sm:text-lg font-serif">
                  Forty-five editorial guides, workbooks &amp; mini-courses on PR,
                  branding, content, social &amp; the founder&apos;s hour — written from
                  the same desk that&apos;s quietly built brands behind Susan Olsen,
                  Julia Post and a hundred more.
                </p>
              </Reveal>

              {/* Sales-conversion ribbon */}
              <Reveal delay={0.28}>
                <div className="mt-7 sm:mt-9 grid grid-cols-2 md:grid-cols-4 gap-0 border-y border-ink/15">
                  {[
                    { label: "Instant access", body: "In your browser the second it clears." },
                    { label: "Lifetime", body: "Yours forever. Every update, free." },
                    { label: "Bundle savings", body: "Any 5 for $129 — saves up to $166." },
                    { label: "Cause-aligned", body: "10% of every sale to a charity partner." },
                  ].map((c, i) => (
                    <div
                      key={c.label}
                      className={`py-4 ${i < 3 ? "md:border-r" : ""} ${i % 2 === 0 ? "border-r md:border-r" : ""} ${i < 2 ? "border-b md:border-b-0" : ""} border-ink/10 px-3`}
                    >
                      <p className="overline text-[9px] tracking-[0.3em] text-ink/55">{c.label}</p>
                      <p className="font-serif italic text-[13px] sm:text-[14px] text-ink/85 leading-snug mt-1">
                        {c.body}
                      </p>
                    </div>
                  ))}
                </div>
              </Reveal>

              {/* CTAs */}
              <Reveal delay={0.4}>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    data-testid="hero-open-quiz"
                    onClick={() => setQuizOpen(true)}
                    className="group inline-flex items-center gap-3 bg-ink text-cream px-6 sm:px-8 py-4 overline text-[10px] tracking-[0.28em] hover:bg-ink/85 transition-colors"
                  >
                    <Sparkles className="w-4 h-4" strokeWidth={1.5}/>
                    Take the 60-second quiz
                    <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"/>
                  </button>
                  <a
                    href="#library"
                    data-testid="hero-browse"
                    className="inline-flex items-center gap-2 px-6 sm:px-8 py-4 overline text-[10px] tracking-[0.28em] border border-ink/40 hover:bg-ink hover:text-cream transition-colors"
                  >
                    Browse all {stats.total} editions →
                  </a>
                </div>
              </Reveal>
            </div>

            {/* RIGHT — featured cover edition */}
            <div className="col-span-12 lg:col-span-5">
              {coverEdition && (
                <Reveal delay={0.15}>
                  <motion.div style={{ y: coverY }}>
                  <Link
                    to={`/studio/${coverEdition.slug}`}
                    data-testid="hero-cover-edition"
                    className="group block"
                  >
                    <motion.div
                      className="relative aspect-[4/5] bg-ink text-cream overflow-hidden"
                      whileHover={{ y: -6 }}
                      transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
                    >
                      {/* Edition number — huge in the background, fully contained */}
                      <div className="absolute -bottom-6 -right-4 z-0 font-display text-[10rem] sm:text-[14rem] lg:text-[18rem] leading-none text-cream/10 select-none pointer-events-none">
                        {coverEdition.edition_no}
                      </div>
                      {/* Cover content */}
                      <div className="relative z-10 h-full flex flex-col p-5 sm:p-7 lg:p-8">
                        <div className="flex items-start justify-between gap-4">
                          <p className="overline text-cream/65 text-[9px] tracking-[0.32em] flex-1 min-w-0">
                            № {coverEdition.edition_no} · {coverEdition.tier}
                          </p>
                          <div className="text-right flex-shrink-0">
                            <p className="overline text-[8px] tracking-[0.28em] text-cream/55 mb-1">Latest issue</p>
                            <p className="font-display text-2xl sm:text-3xl">${coverEdition.price}</p>
                          </div>
                        </div>
                        <div className="flex-1 flex items-end">
                          <div className="w-full">
                            <h2 className="font-display text-[1.7rem] sm:text-3xl lg:text-5xl leading-[0.94] mb-3">
                              {coverEdition.title}
                            </h2>
                            <p className="font-serif italic text-cream/75 text-sm sm:text-base leading-snug max-w-sm">
                              {coverEdition.blurb}
                            </p>
                            <div className="mt-5 sm:mt-6 inline-flex items-center gap-2 overline text-[10px] tracking-[0.28em] text-cream pb-1 border-b border-cream/40 group-hover:border-cream transition-colors">
                              Read this issue
                              <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"/>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                    <div className="mt-3 flex items-baseline justify-between text-[10px] tracking-[0.25em] uppercase text-ink/55">
                      <span>The Cover · this month</span>
                      <span className="font-serif italic normal-case tracking-normal text-ink/70 text-xs">tap to read →</span>
                    </div>
                  </Link>
                  </motion.div>
                </Reveal>
              )}
            </div>
          </div>
        </div>

        {/* ── Live activity ticker — black bar, white serif italic, slow crossfade ── */}
        <div className="relative z-10 bg-ink text-cream py-3 overflow-hidden">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 flex items-center gap-3 sm:gap-6">
            <span className="overline text-cream/55 text-[9px] tracking-[0.4em] flex-shrink-0">
              · ON THE FLOOR ·
            </span>
            <div className="relative flex-1 h-5 overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.p
                  key={tickerIdx}
                  initial={{ y: 18, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -18, opacity: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="absolute inset-0 font-serif italic text-cream/95 text-sm sm:text-base truncate"
                >
                  {tickerLines[tickerIdx]}
                </motion.p>
              </AnimatePresence>
            </div>
            <span className="overline text-cream/55 text-[9px] tracking-[0.4em] flex-shrink-0">
              · LIVE ·
            </span>
          </div>
        </div>
      </section>

      {/* ───── COLLECTIONS GRID ───── */}
      <section className="py-20 sm:py-28 border-b border-ink/10">
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-ink/60 mb-3">
                Six Collections
              </p>
              <h2 className="font-display text-4xl sm:text-6xl text-ink">
                Find your <span className="font-serif italic font-light">corner of the work.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {categories.map((c, i) => {
              const Icon = CAT_ICON[c.key] || BookOpen;
              const count = products.filter(p => p.category === c.key).length;
              return (
                <Reveal key={c.key} delay={i * 0.05}>
                  <TiltCard className="will-change-transform">
                  <button
                    type="button"
                    data-testid={`collection-${c.key}`}
                    onClick={() => {
                      setActiveCat(c.key);
                      document.getElementById("library")?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="group relative block w-full text-left h-56 sm:h-64 overflow-hidden bg-ink text-cream transition-transform duration-500"
                  >
                    <div
                      className="absolute inset-0 opacity-20 group-hover:opacity-40 transition-opacity"
                      style={{ background: `radial-gradient(circle at 30% 20%, ${c.color}, transparent 70%)` }}
                    />
                    <div className="relative h-full p-7 flex flex-col justify-between" style={{ transform: "translateZ(40px)" }}>
                      <Icon className="w-7 h-7 text-cream/70 group-hover:text-cream" strokeWidth={1.2}/>
                      <div>
                        <p className="text-[9px] tracking-[0.3em] uppercase text-cream/50 mb-2">
                          {count} editions
                        </p>
                        <h3 className="font-display text-3xl leading-tight mb-1">
                          {c.label}
                        </h3>
                        <p className="font-serif italic text-cream/70 text-sm">
                          {c.subtitle}
                        </p>
                      </div>
                    </div>
                  </button>
                  </TiltCard>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───── FOUNDER HOUR — full-bleed editorial banner ───── */}
      <section className="bg-ink text-cream py-16 sm:py-20 lg:py-24 border-y border-cream/10 relative overflow-hidden" data-testid="founder-hour-banner">
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Left rail — pitch */}
            <div className="lg:col-span-7">
              <div className="overline text-cream/55 mb-4 flex flex-wrap items-center gap-3">
                <span className="bg-cream text-ink px-2 py-0.5 text-[10px] tracking-[0.25em]">Edition C01</span>
                <span>Limited · 10 founders this quarter</span>
              </div>
              <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.85]">
                The Founder Hour<br/>
                <span className="font-serif italic normal-case text-cream/90">+ the entire library</span>.
              </h2>
              <p className="font-serif italic text-xl sm:text-2xl text-cream/80 mt-6 max-w-xl">
                One private hour with Meghan. <span className="not-italic">Every paid edition we&apos;ve ever published</span>, unlocked in your shelf the moment payment clears.
              </p>

              <ul className="mt-8 space-y-2 text-sm text-cream/85" data-testid="founder-hour-stack">
                <li>· One 60-minute private strategy session</li>
                <li>· 34 paid editions — playbooks, ebooks, audio series, workbooks, guides</li>
                <li>· Lifetime access in your private M Media library</li>
                <li>· Welcome email + Calendly invitation within 5 business days</li>
              </ul>
            </div>

            {/* Right rail — value stack + CTA card */}
            <div className="lg:col-span-5">
              <div className="bg-cream text-ink p-6 sm:p-8 shadow-2xl">
                <p className="overline text-ink/55 mb-4">Value stack</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between"><span>Private 60-min call</span><span className="tabular-nums text-ink/65">$1,000</span></li>
                  <li className="flex justify-between"><span>21 × Playbooks ($59)</span><span className="tabular-nums text-ink/65">$1,239</span></li>
                  <li className="flex justify-between"><span>8 × Mini-Courses &amp; Audio</span><span className="tabular-nums text-ink/65">$312</span></li>
                  <li className="flex justify-between"><span>2 × Ebooks</span><span className="tabular-nums text-ink/65">$98</span></li>
                  <li className="flex justify-between"><span>3 × Workbooks + Guide</span><span className="tabular-nums text-ink/65">$73</span></li>
                  <li className="flex justify-between pt-3 border-t border-ink/15">
                    <span className="font-medium">Total stack value</span>
                    <span className="font-display text-2xl">$2,722</span>
                  </li>
                </ul>

                <div className="mt-6 pt-6 border-t border-ink/15">
                  <div className="flex items-baseline justify-between mb-1">
                    <p className="overline text-ink/55">Founder rate</p>
                    <p className="font-display text-5xl leading-none">$997</p>
                  </div>
                  <p className="font-serif italic text-sm text-ink/65 text-right">You save $1,725 · 63% off</p>
                </div>

                <Link
                  to="/studio/coaching/apply"
                  data-testid="founder-hour-cta"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-ink text-cream py-4 text-xs tracking-[0.25em] uppercase hover:bg-ink/85 transition-colors"
                >
                  Claim my hour →
                </Link>
                <p className="text-[10px] tracking-[0.22em] uppercase text-ink/45 mt-3 text-center">
                  All sales final · Limited to 10 founders this quarter
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {freeStarters.length > 0 && (
        <section className="py-20 sm:py-24 border-b border-ink/10 bg-white">
          <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-ink/60 mb-3">
                  Start here · Free
                </p>
                <h2 className="font-display text-4xl sm:text-5xl text-ink">
                  Five files. <span className="font-serif italic font-light">No card required.</span>
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {freeStarters.slice(0, 5).map((p, i) => (
                <ProductCard
                  key={p.slug}
                  p={p}
                  idx={i}
                  isSaved={isSaved(p.slug)}
                  onToggleSave={toggle}
                  bundleMode={bundleMode}
                  selected={bundleSelection.includes(p.slug)}
                  onToggleBundle={toggleBundle}
                  disabledForBundle={bundleMode && (p.is_free || p.no_download)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ───── EDITOR'S PICKS ───── */}
      {featured.length > 0 && (
        <section className="py-20 sm:py-24 border-b border-ink/10">
          <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-ink/60 mb-3">
                  Editor&apos;s Picks
                </p>
                <h2 className="font-display text-4xl sm:text-5xl text-ink">
                  The eight we&apos;d <span className="font-serif italic font-light">send first.</span>
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {featured.map((p, i) => (
                <ProductCard
                  key={p.slug}
                  p={p}
                  idx={i}
                  isSaved={isSaved(p.slug)}
                  onToggleSave={toggle}
                  bundleMode={bundleMode}
                  selected={bundleSelection.includes(p.slug)}
                  onToggleBundle={toggleBundle}
                  disabledForBundle={bundleMode && (p.is_free || p.no_download)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ───── FULL LIBRARY ───── */}
      <section id="library" className="py-20 sm:py-24">
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="mb-10">
            <p className="text-[10px] tracking-[0.3em] uppercase text-ink/60 mb-3">
              The Full Library
            </p>
            <h2 className="font-display text-4xl sm:text-6xl text-ink">
              All {stats.total} <span className="font-serif italic font-light">editions.</span>
            </h2>
          </div>

          {/* Search + filters */}
          <div className="sticky z-30 -mx-6 px-6 sm:-mx-10 sm:px-10 lg:-mx-16 lg:px-16 bg-cream/95 backdrop-blur py-4 border-y border-ink/10 mb-8" style={{ top: "84px" }}>
            <p className="overline text-ink/45 text-[10px] tracking-[0.3em] mb-3">
              Filter the catalog
            </p>
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/40" strokeWidth={1.5}/>
                <input
                  type="text"
                  data-testid="library-search"
                  placeholder="Search title, topic, tier…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full bg-white border border-ink/15 pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-ink/50"
                />
              </div>
              <select
                data-testid="library-tier-select"
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="bg-white border border-ink/15 px-4 py-3 text-sm focus:outline-none focus:border-ink/50"
              >
                {tiers.map(t => <option key={t} value={t}>{t === "all" ? "All tiers" : t}</option>)}
              </select>
            </div>

            {/* Category chips */}
            <div className="flex flex-wrap gap-2 mt-3">
              {[
                { k: "all", label: `Everything · ${products.length}` },
                { k: "free", label: `Free · ${stats.free}` },
                { k: "saved", label: `Saved · ${saved.length}` },
                ...categories.map(c => ({ k: c.key, label: `${c.label} · ${products.filter(p => p.category === c.key).length}` })),
              ].map(({ k, label }) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`chip-${k}`}
                  onClick={() => setActiveCat(k)}
                  className={`text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border transition-colors ${
                    activeCat === k
                      ? "bg-ink text-cream border-ink"
                      : "bg-white text-ink border-ink/15 hover:border-ink/40"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20 border border-ink/10 bg-white">
              <p className="font-serif italic text-2xl text-ink/60">Nothing matches yet.</p>
              <button
                type="button"
                onClick={() => { setSearch(""); setActiveCat("all"); setTierFilter("all"); }}
                className="mt-6 text-xs tracking-[0.2em] uppercase underline text-ink/60 hover:text-ink"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((p, i) => (
                <ProductCard
                  key={p.slug}
                  p={p}
                  idx={i}
                  isSaved={isSaved(p.slug)}
                  onToggleSave={toggle}
                  bundleMode={bundleMode}
                  selected={bundleSelection.includes(p.slug)}
                  onToggleBundle={toggleBundle}
                  disabledForBundle={bundleMode && (p.is_free || p.no_download)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* The old "Let us pick three" CTA has moved to the new closing-sales band below. */}

      <StudioQuiz open={quizOpen} onClose={() => setQuizOpen(false)} />

      {/* ───── LIBRARY CARD ─ existing buyers slip in here ───── */}
      <section className="py-20 sm:py-24 bg-ink text-cream relative overflow-hidden" data-testid="studio-library-card">
        {/* Soft ambient grain */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,1) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}/>
        <div className="relative max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8 lg:gap-14 items-center">
            <div className="col-span-12 lg:col-span-7">
              <p className="overline text-cream/55 mb-4 text-[10px] tracking-[0.4em]">
                The reading room · already a member?
              </p>
              <h2 className="font-display text-4xl sm:text-6xl leading-[0.94] mb-5">
                Your shelf is <span className="font-serif italic font-light">already open.</span>
              </h2>
              <p className="font-serif text-cream/75 text-lg leading-relaxed mb-8 max-w-xl">
                Type the email you used at checkout — we&apos;ll open every edition you&apos;ve ever bought. No password, no email round-trip. Lifetime access, on any device.
              </p>
              <ul className="space-y-2 mb-2 text-sm text-cream/70">
                <li className="flex items-center gap-3">
                  <span className="w-1 h-1 rounded-full bg-cream/60"/>
                  Edition reader · in-browser PDF, swipe to flip
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1 h-1 rounded-full bg-cream/60"/>
                  Editor&apos;s Toolkit · 12 copy-paste templates unlocked
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-1 h-1 rounded-full bg-cream/60"/>
                  Field Notes · saved reflections sync across devices
                </li>
              </ul>
            </div>
            <div className="col-span-12 lg:col-span-5">
              <LibraryCardInlineForm/>
            </div>
          </div>
        </div>
      </section>

      {/* ───── EDITORIAL TESTIMONIALS ───── */}
      <section className="py-20 sm:py-28 border-b border-ink/10 bg-cream" data-testid="studio-testimonials">
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8 lg:gap-12">
            <div className="col-span-12 lg:col-span-4">
              <p className="overline text-ink/55 mb-3 text-[10px] tracking-[0.4em]">
                Founders on the floor
              </p>
              <h2 className="font-display text-4xl sm:text-5xl leading-[0.94] mb-5">
                Read in <span className="font-serif italic font-light">writing rooms,</span> not waiting rooms.
              </h2>
              <p className="font-serif text-ink/65 leading-relaxed">
                Editions get pulled, marked up, and quoted back the next day. The shelf below is real — every line lifted from a buyer&apos;s note. Nothing rented from a stock-photo studio.
              </p>
            </div>
            <div className="col-span-12 lg:col-span-8 grid sm:grid-cols-2 gap-4 sm:gap-5">
              {[
                { q: "The hashtag system alone repaid the edition in a week. I screenshot it twice a month.", a: "Olivia · founder, jewelry brand", ed: "№ 008 Content Marketing Accelerator" },
                { q: "I sent the cold pitch on Tuesday. We were in Forbes by Friday. Genuinely don't know what to do with that.", a: "Mariana · CEO, supplement brand", ed: "Editor's Toolkit · PR cards" },
                { q: "The brand voice fingerprint cleaned up six months of mixed messaging in an afternoon.", a: "Susan · partner, holding co.", ed: "№ 020 Brand Evolution System" },
                { q: "I bought five for $129 and walked away with a launch plan I'd have paid $5k for.", a: "Jen · co-founder, B2B SaaS", ed: "Bundle of 5" },
              ].map((t, i) => (
                <motion.figure
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.7, delay: i * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
                  className="bg-white border border-ink/10 p-6 sm:p-7"
                >
                  <blockquote className="font-serif text-ink/90 text-lg sm:text-xl leading-snug mb-5">
                    &ldquo;{t.q}&rdquo;
                  </blockquote>
                  <figcaption className="flex items-baseline justify-between gap-3 border-t border-ink/10 pt-3">
                    <span className="overline text-ink/55 text-[10px] tracking-[0.25em]">{t.a}</span>
                    <span className="font-serif italic text-ink/60 text-xs">{t.ed}</span>
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───── FAQ — sales-conversion answers, organized as a magazine Q&A ───── */}
      <section className="py-20 sm:py-28 border-b border-ink/10 bg-white" data-testid="studio-faq">
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8 lg:gap-12">
            <div className="col-span-12 lg:col-span-4">
              <p className="overline text-ink/55 mb-3 text-[10px] tracking-[0.4em]">
                Reading-room rules
              </p>
              <h2 className="font-display text-4xl sm:text-5xl leading-[0.94] mb-5">
                The <span className="font-serif italic font-light">honest</span> Q &amp; A.
              </h2>
              <p className="font-serif text-ink/65 leading-relaxed">
                The exact questions buyers ask before they reach for their card. Answered the way the editor would over coffee.
              </p>
              <Link
                to="/services"
                data-testid="faq-services-link"
                className="inline-flex items-center gap-2 mt-8 overline text-[10px] tracking-[0.28em] underline-offset-4 underline hover:no-underline"
              >
                Need bespoke work? See services
                <ArrowUpRight className="w-3 h-3"/>
              </Link>
            </div>
            <div className="col-span-12 lg:col-span-8 space-y-0">
              {[
                {
                  q: "Is this really just $19–$49 each? What's the catch?",
                  a: "There isn't one. The catalog is priced to be a no-brainer for any founder serious about their craft. We make money on the bundle ($129 for any 5), the Founder's Hour ($997), and on referrals back to bespoke services. The editions are the front door, not the bottom line.",
                },
                {
                  q: "How is this different from a course on Skillshare or MasterClass?",
                  a: "Length. These are 20–80 page editorial workbooks you finish in an afternoon, not 8-hour video marathons. They're written for founders who don't have time to watch — they have time to read, mark up, and act. The Toolkit drawer inside every edition gives you copy-paste templates the moment you're done.",
                },
                {
                  q: "What if it's not for me?",
                  a: "Try ten free starters first — that's why they exist. You don't need permission to read them. If a paid edition doesn't land, write to the desk at info@mmediacorp.com within 14 days. We'll make it right, no friction.",
                },
                {
                  q: "Can I read it on my phone?",
                  a: "Yes. Every edition reads natively in your mobile browser. Swipe left/right to flip pages, pinch to zoom. No app, no email round-trip, no PDF download — it's already on your shelf the second you pay.",
                },
                {
                  q: "What's actually inside the Editor's Toolkit?",
                  a: "Twelve hand-authored cards across PR, Branding, Marketing, Sales, and Social — copy-paste-ready cold pitches, post hooks, brand voice fingerprints, hashtag systems, launch runways, objection-handler scripts. Unlocked the moment you own a single paid edition. Permanently yours.",
                },
                {
                  q: "Bundle math — is it actually a deal?",
                  a: "Yes. Five editions à la carte run $145–$245 depending on the mix; the bundle is $129 flat. The math is built to reward people who want the full toolkit. If you only want one, buy one — no pressure.",
                },
                {
                  q: "Who writes these?",
                  a: "Meghan Amaral and the M Media editorial desk. Twelve years in PR and brand strategy with clients across hospitality, jewelry, fashion, and B2B. Every edition is real working-document material — what the desk would give a client paying $5k.",
                },
                {
                  q: "What happens after I buy?",
                  a: "Stripe clears the payment, we issue your library card automatically, and the edition is on your shelf the second the page loads. You'll get a Stripe receipt by email; the edition itself lives in your browser — accessible from any device with your email.",
                },
              ].map((f, i) => (
                <details
                  key={i}
                  className="group border-t border-ink/10 last:border-b py-5 sm:py-6"
                  data-testid={`faq-item-${i}`}
                >
                  <summary className="cursor-pointer list-none flex items-start gap-6 sm:gap-8 justify-between">
                    <h3 className="font-display text-lg sm:text-xl leading-snug uppercase tracking-tight pr-4">
                      {f.q}
                    </h3>
                    <span className="font-serif italic text-2xl text-ink/55 group-open:rotate-45 transition-transform flex-shrink-0">+</span>
                  </summary>
                  <p className="mt-4 sm:mt-5 font-serif text-ink/75 text-base sm:text-[17px] leading-relaxed max-w-3xl">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───── CLOSING SALES BAND ───── */}
      <section className="bg-ink text-cream py-16 sm:py-20" data-testid="studio-closing">
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-12 gap-6 items-center">
          <div className="col-span-12 lg:col-span-7">
            <p className="overline text-cream/55 mb-3 text-[10px] tracking-[0.4em]">
              One last thing
            </p>
            <h2 className="font-display text-4xl sm:text-6xl leading-[0.94]">
              The five editions <span className="font-serif italic font-light">most likely</span> to change your next 30 days.
            </h2>
            <p className="font-serif text-cream/70 text-lg mt-5 max-w-xl leading-snug">
              Sixty seconds. Five questions. No email required. We&apos;ll match you to the three editions worth starting with, with the math on the bundle if it makes sense.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-5 flex flex-col sm:flex-row gap-3 lg:justify-end">
            <button
              type="button"
              data-testid="closing-open-quiz"
              onClick={() => setQuizOpen(true)}
              className="inline-flex items-center justify-center gap-3 bg-cream text-ink px-7 py-4 overline text-[10px] tracking-[0.28em] hover:bg-cream/90 transition-colors"
            >
              <Sparkles className="w-4 h-4" strokeWidth={1.5}/>
              Take the 60-second quiz
            </button>
            <button
              type="button"
              data-testid="closing-open-bundle"
              onClick={() => setBundleMode(true)}
              className="inline-flex items-center justify-center gap-3 px-7 py-4 overline text-[10px] tracking-[0.28em] border border-cream/40 hover:bg-cream hover:text-ink transition-colors"
            >
              Build a Bundle · 5 for $129
            </button>
          </div>
        </div>
      </section>

      {/* ───── PICK-5 BUNDLE — entry button (floating, top-right when scrolled) ───── */}
      {!bundleMode && (
        <button
          type="button"
          data-testid="bundle-mode-enter"
          onClick={() => setBundleMode(true)}
          className="fixed bottom-6 right-6 z-40 bg-ink text-cream px-5 py-4 text-xs tracking-[0.22em] uppercase font-medium shadow-2xl hover:bg-ink/90 transition-transform hover:-translate-y-0.5"
        >
          Build a Bundle · 5 for $129
        </button>
      )}

      {/* ───── PICK-5 STICKY BAR ───── */}
      {bundleMode && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-ink text-cream border-t border-cream/15 shadow-2xl" data-testid="bundle-bar">
          {upsellSlug && (
            <div className="bg-gradient-to-r from-amber-200/15 via-cream/10 to-amber-200/15 border-b border-cream/15" data-testid="upsell-banner">
              <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-2.5 text-center">
                <p className="text-[10px] sm:text-[11px] tracking-[0.22em] uppercase text-cream/85">
                  <span className="font-serif italic normal-case tracking-normal text-cream">The desk&apos;s offer</span>
                  <span className="mx-2 text-cream/40">·</span>
                  Add 4 more editions and your full bundle is $129 — saving you $36+ vs buying separately.
                </p>
              </div>
            </div>
          )}
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 py-4 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="font-display text-xl sm:text-2xl">
                {bundleSelection.length}<span className="text-cream/40"> / 5</span>
              </div>
              <div className="text-xs sm:text-sm text-cream/70 truncate">
                {bundleSelection.length === 0 && "Pick any 5 paid editions — flat $129 (save up to $166)."}
                {bundleSelection.length > 0 && bundleSelection.length < 5 && `Pick ${5 - bundleSelection.length} more to unlock checkout.`}
                {bundleSelection.length === 5 && "Bundle ready. $129 — your best move."}
              </div>
            </div>
            <button
              type="button"
              onClick={exitBundle}
              className="text-[10px] tracking-[0.22em] uppercase text-cream/55 hover:text-cream"
              data-testid="bundle-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={bundleSelection.length !== 5}
              onClick={() => setBundleModalOpen(true)}
              data-testid="bundle-checkout-open"
              className={`px-5 sm:px-7 py-3 text-xs tracking-[0.22em] uppercase font-medium transition-colors ${
                bundleSelection.length === 5
                  ? "bg-cream text-ink hover:bg-cream/90"
                  : "bg-cream/20 text-cream/40 cursor-not-allowed"
              }`}
            >
              Checkout · $129
            </button>
          </div>
        </div>
      )}

      <BundleCheckoutModal
        open={bundleModalOpen}
        slugs={bundleSelection}
        products={products}
        onClose={() => setBundleModalOpen(false)}
        onSuccess={() => { setBundleSelection([]); setBundleMode(false); setBundleModalOpen(false); }}
      />
    </div>
  );
}

// ────────────────────────────────────────────
// PICK-5 CHECKOUT MODAL
// ────────────────────────────────────────────
function BundleCheckoutModal({ open, slugs, products, onClose, onSuccess }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [brand, setBrand] = useState("");
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selectedItems = useMemo(
    () => slugs.map(s => products.find(p => p.slug === s)).filter(Boolean),
    [slugs, products]
  );

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!terms) { toast.error("Please accept the no-refunds terms."); return; }
    if (slugs.length !== 5) { toast.error("Pick exactly 5 editions."); return; }
    setSubmitting(true);
    try {
      const { data } = await postWithRetry(`${API}/api/studio/bundle-checkout`, {
        slugs, name, email, brand: brand || null,
        origin_url: window.location.origin,
        terms_accepted: true,
      }, { timeout: 18000 });
      onSuccess();
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur grid place-items-center px-4 py-8 overflow-y-auto" data-testid="bundle-modal">
      <div className="bg-cream w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 p-2 text-ink/60 hover:text-ink" aria-label="Close">
          <X size={20}/>
        </button>
        <div className="p-8 sm:p-12">
          <p className="overline text-ink/60 mb-2">Custom · Pick-5</p>
          <h2 className="font-display text-4xl sm:text-5xl text-ink leading-[0.95] mb-4">
            Your bundle, <span className="serif-italic font-serif normal-case">made yours</span>.
          </h2>
          <p className="text-ink/70 mb-6 text-sm">Five editions, one transaction, $129 flat. Opens instantly in your private library.</p>

          <div className="border-y border-ink/15 py-4 mb-6">
            <p className="overline text-ink/55 mb-3">In your bundle</p>
            <ul className="space-y-1.5 text-sm">
              {selectedItems.map((p) => (
                <li key={p.slug} className="flex items-start gap-3">
                  <span className="text-ink/40 text-[10px] tracking-wider mt-1">No. {p.edition_no}</span>
                  <span className="text-ink flex-1">{p.title}</span>
                </li>
              ))}
            </ul>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="overline text-ink/60">Full name *</span>
                <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="bundle-name"
                  className="mt-1 w-full border-b border-ink/30 bg-transparent py-2 outline-none focus:border-ink text-ink"/>
              </label>
              <label className="block">
                <span className="overline text-ink/60">Email *</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="bundle-email"
                  className="mt-1 w-full border-b border-ink/30 bg-transparent py-2 outline-none focus:border-ink text-ink"/>
              </label>
            </div>
            <label className="block">
              <span className="overline text-ink/60">Brand / Company (optional)</span>
              <input value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="bundle-brand"
                className="mt-1 w-full border-b border-ink/30 bg-transparent py-2 outline-none focus:border-ink text-ink"/>
            </label>
            <label className="flex items-start gap-3 text-sm text-ink/80 cursor-pointer pt-2" data-testid="bundle-terms-row">
              <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1" data-testid="bundle-terms"/>
              <span>I understand all sales are final — <strong>no refunds, no exchanges.</strong> Editions deliver instantly upon successful payment.</span>
            </label>
            <button
              type="submit"
              disabled={submitting || !terms}
              data-testid="bundle-checkout-submit"
              className={`w-full mt-4 py-4 text-xs tracking-[0.25em] uppercase font-medium transition-colors ${
                submitting || !terms ? "bg-ink/30 text-cream cursor-not-allowed" : "bg-ink text-cream hover:bg-ink/90"
              }`}
            >
              {submitting ? "Opening Stripe…" : "Pay $129 with Stripe"}
            </button>
            <p className="text-[10px] tracking-[0.2em] uppercase text-ink/40 text-center pt-2">
              Stripe-secured · PCI compliant
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
